import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import customerUsersApi from "./api/customerUsersApi";
import {
  InviteUser,
  InviteTemporaryUser,
  UserRole,
  UserState,
  EnableUser,
  DisableUser,
  ReinviteUser,
  PinRegenerate
} from "./models/userModels";

export const getUsers = createAsyncThunk(
  "customerUsers/getUsers",
  async () => {
    return customerUsersApi.getUsers();
  }
);

export const inviteUser = createAsyncThunk(
  "customerUsers/inviteUser",
  async (model: InviteUser) => {
    return customerUsersApi.inviteUser(model);
  }
);

export const reinviteUser = createAsyncThunk(
  "customerUsers/reinviteUser",
  async (model: ReinviteUser) => {
    return customerUsersApi.reinviteUser(model);
  }
);

export const pinRegenerate = createAsyncThunk(
  "customerUsers/pinRegenerate",
  async (model: PinRegenerate) => {
    return customerUsersApi.pinRegenerate(model);
  }
);
export const grantTemporaryAccess = createAsyncThunk(
  "customerUsers/grantTemporaryAccess",
  async (model: InviteTemporaryUser) => {
    return customerUsersApi.inviteTemporaryUser(model);
  }
);

export const enableUser = createAsyncThunk(
  "customerUsers/enableUser",
  async (model: EnableUser) => {
    return customerUsersApi.enableUser(model);
  }
);

export const disableUser = createAsyncThunk(
  "customerUsers/disableUser",
  async (model: DisableUser) => {
    return customerUsersApi.disableUser(model);
  }
);

export const deleteUser = createAsyncThunk(
  "customerUsers/deleteUser",
  async (data: { id: string; }) => {
    return customerUsersApi.deleteUser(data.id);
  }
);

export const getRoles = createAsyncThunk(
  "customerUsers/getRoles",
  async () => {
    return customerUsersApi.getRoles();
  }
);

export const changeUserRole = createAsyncThunk(
  "customerUsers/changeUserRole",
  async (data: { userId: string; roleId: string }) => {
    return customerUsersApi.changeUserRole(data.userId, { roleId: data.roleId });
  }
);

const initialState: UserState = {
  users: [],
  roles: [],
  loading: false,
  error: undefined,
  success: undefined,
  sortBy: undefined,
  sortDesc: false
};

const customerUsersSlice = createSlice({
  name: "customerUsers",
  initialState: initialState,
  reducers: {
    clearError: (state) => {
      state.error = undefined;
    },
    clearSuccess: (state) => {
      state.success = undefined;
    },
    setSort: (state, action: PayloadAction<{ sortBy: number, sortDesc: boolean }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortDesc = action.payload.sortDesc;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUsers.fulfilled, (state, { payload }) => {
        state.users = payload;
        state.loading = false;
      })
      .addCase(getUsers.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(inviteUser.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(inviteUser.fulfilled, (state, { meta }) => {
        state.loading = false;
        state.success = {
          key: "permanentUserInvited",
          params: { userName: meta.arg.userName || "User" }
        };
      })
      .addCase(inviteUser.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(reinviteUser.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(reinviteUser.fulfilled, (state, { meta }) => {
        state.loading = false;
        const user = state.users.find(u => u.id === meta.arg.userId);
        state.success = {
          key: "reinviteUser",
          params: { userName: user?.userName || "User" }
        };
      })
      .addCase(reinviteUser.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(pinRegenerate.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(pinRegenerate.fulfilled, (state, { meta }) => {
        state.loading = false;
        const user = state.users.find(u => u.id === meta.arg.userId);
        state.success = {
          key: "pinRegenerated",
          params: { userName: user?.userName || "User" }
        };
      })
      .addCase(pinRegenerate.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(grantTemporaryAccess.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(grantTemporaryAccess.fulfilled, (state, { meta }) => {
        state.loading = false;
        state.success = {
          key: "temporaryUserInvited",
          params: { userName: meta.arg.userName || "User" }
        };
      })
      .addCase(grantTemporaryAccess.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(deleteUser.fulfilled, (state, { meta }) => {
        state.loading = false;
        const user = state.users.find(u => u.id === meta.arg.id);
        state.success = {
          key: "userDeleted",
          params: { userName: user?.userName || "User" }
        };
      })
      .addCase(deleteUser.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(enableUser.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(enableUser.fulfilled, (state, { meta }) => {
        state.loading = false;
        const user = state.users.find(u => u.id === meta.arg.userId);
        state.success = {
          key: "temporaryUserActivated",
          params: { userName: user?.userName || "User" }
        };
      })
      .addCase(enableUser.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(disableUser.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(disableUser.fulfilled, (state, { meta }) => {
        state.loading = false;
        const user = state.users.find(u => u.id === meta.arg.userId);
        state.success = {
          key: "temporaryUserDeactivated",
          params: { userName: user?.userName || "User" }
        };
      })
      .addCase(disableUser.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getRoles.fulfilled, (state, { payload }) => {
        state.roles = payload;
      });

    builder
      .addCase(changeUserRole.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(changeUserRole.fulfilled, (state, { meta }) => {
        state.loading = false;
        const user = state.users.find(u => u.id === meta.arg.userId);
        state.success = {
          key: "roleChanged",
          params: { userName: user?.userName || "User" }
        };
      })
      .addCase(changeUserRole.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  }
});

export const {
  clearError,
  clearSuccess,
  setSort
} = customerUsersSlice.actions;

export default customerUsersSlice.reducer;